const _ = require("lodash");
const moment = require("moment");
const Big = require("big.js");
const ShopData = require("./data/dbShop.json");
const FeeData = require("./data/feeData.json");
const ShopBundleData = require("./data/dbShopBundle.json");
const states = require("./data/states.json");
const countries = require("./data/countries.json");
const Constants = require("./Constants");
const {checkEmailPassword} = require("./PasswordCheck");
const {
	isValidEmail,
	isValidCheckoutString,
	toPrice,
	isZipcodeCharacters,
} = require("./StringUtil");
const StoreBundleUtil = require("./StoreBundleUtil");
const CouponUtil = require("./CouponUtil");
const SharedErrorLog = require("./SharedErrorLog");
const {groupBooksIntoSets} = require("../shared/BookstoreUtil");
const {SubscriptionSkuEnum} = require("./StoreSkuUtil");
const {SUBSCRIPTION_CODE_SKU} = SubscriptionSkuEnum;

const SHIPPABLE_TYPES = ["book", "apparel"];
const USA = "US";
const CANADA = "CA";
const BRAZIL = "BR";
const PRIORITY_SHIPPING_STATES = [
	"AP",
	"PR",
	"GU",
	"AS",
	"FM",
	"MH",
	"MP",
	"PW",
	"VI",
	"AE",
	"AA",
];
const {MAX_SUBSCRIPTIONS_FOR_PARENT, MAX_DISPLAY_NAME_LENGTH} = Constants;

// IMPORANT: If you update this array or anything to do with addresses blocked
// for shipping, you should also edit the message shown in
// office/data/CopyTable's getShippingNotice.
const COUNTRIES_WITH_BLOCKED_SHIPPING = ["RU", "BY", "C2", "AE", "SA"];

// U.S. States that levy an extra fee for physical goods shipped into the state.
// See shared/FeeData.json for more info on the fee.
// This fee gets treated as a $0 item in the cart, even though it is not an
// actual product.
// if modifying to add shipping fees to another state, remember to update the
// automated tests as well
const STATE_SHIPPING_FEES = {};

const getTaxCodes = function() {
	return {
		book: "PS060303",
		puzzlebook: "PB100818",
		apparel: "PC040100",
		subscription: "ST080031",
		shipping: "FR030000",
		shippingfee: "OF400000",
		code: "ST080031", // same as subscription
		gift: "ST080031", // same as subscription
	};
};

//Functions that run on types of items or single items from the cart:

//items are always an object with required keys sku and quantity that can optionally have
//other keys that specify choices for the product like size and color
//examples {sku: "beast-3a", quantity: 3} and {sku: "tshirt-grogg", quantity: 1, size: "large", color: "red"}

// Gets the display name of a product from its sku
const getNameFromSKU = function(sku) {
	const itemData = getItemDataFromSku(sku);
	return itemData.name;
};

//Gets the type of a product from its sku
const getTypeFromSKU = function(sku) {
	if (!sku) return null;
	const itemData = getItemDataFromSku(sku);
	if (_.includes(sku, "bundle")) {
		return "bundle";
	} else if (_.includes(sku, "full-set")) {
		return "book";
	} else if (_.includes(sku, "gift")) {
		return "gift";
	}
	return itemData.type;
};

// We get fee data (such as an extra state shipping fee) from feeData.json.
// The data for regular items (actual store products) comes from shopData.json.
const getItemDataFromSku = function(sku) {
	if (isSkuForFee(sku)) {
		return FeeData[sku];
	}
	if (ShopBundleData[sku]) {
		return ShopBundleData[sku];
	}
	return ShopData[sku];
};

// Returns true if this is the sku for an extra fee (state shipping taxes)
const isSkuForFee = function(sku) {
	return !!FeeData[sku];
};

// Returns true if this item is an extra fee
const isFeeItem = function(item) {
	return isSkuForFee(item.sku);
};

const getShippingFeeItemData = function(items) {
	const shippingFeeItems = getShippingFeeItems(items);
	return shippingFeeItems.map(item => FeeData[item.sku]);
};

const getShippingFeeItems = function(items) {
	return items.filter(
		item => isFeeItem(item) && getTypeFromSKU(item.sku) === "shippingfee"
	);
};

//Gets subscription sku from subscription plan type
const getSKUFromSubscription = function(subscription) {
	if (subscription.planType) {
		return subscription.planType === "monthly"
			? "beast-monthly"
			: "beast-yearly";
	}
	if (subscription.sku) {
		return subscription.sku;
	}
	return null;
};

//Gets SKUs from given bundle
const getSKUsFromBundleID = function(bundleID) {
	return ShopBundleData[bundleID].skus;
};

//Gets the type of an item
const getItemType = function(item) {
	return getTypeFromSKU(item.sku);
};

//Gets the display name of an item
const getItemName = function(item) {
	if (_.includes(item.sku, "bundle")) {
		return ShopBundleData[item.sku].displayName;
	} else if (isScienceFullSet(item)) {
		const bookCount = item.subitems.length;
		return `Level ${item.grade} Science (${bookCount}-book set)`;
	} else if (isFullSet(item)) {
		const bookCount = item.subitems.length;
		return `Level ${item.grade} (${bookCount}-book set)`;
	} else {
		return getNameFromSKU(item.sku);
	}
};

//Checks if an item is a book item
const isBookItem = function(item) {
	if (!item) return false;
	return getItemType(item) === "book";
};

const isPuzzleItem = function(item) {
	if (!isBookItem(item)) return false;
	return _.includes(item.sku, "puzzle");
};

const isScienceItem = function(item) {
	if (!item) return false;
	return _.includes(item.sku, "science");
};

// Checks if the item is any kind of subscription type
// should include bundles (which already included in isSubscriptionItem) and any type of code that redeems for
// a subscription.
// If gift is ever broadened to include non-codes, this needs to be
// adjusted.
const isAnySubscriptionTypeItem = function(item) {
	if (!item) return false;
	return isSubscriptionItem(item) || isGiftItem(item) || isCodeItem(item);
};

//Checks if an item is a subscription item
const isSubscriptionItem = function(item) {
	if (!item) return false;
	return getItemType(item) === "subscription";
};

// A bundle subscription whose book level is deferred (placement test flow). The
// cart holds the full provisional bundle (subscription + book lines, all with a
// real options.bundleID); the bookLevelDeferred flag only marks that the grade
// may still change before the order is transferred for fulfillment. Pricing,
// shipping, and tax treat it as an ordinary bundle — the flag has no effect on
// any of that. It only gates the AoPS transfer (aopsStatus) and tells the
// resolve flow which subscription's books may need to be swapped.
const isDeferredBundleSubscription = function(item) {
	if (!item) return false;
	return isSubscriptionItem(item) && !!_.get(item, "options.bookLevelDeferred");
};

//Checks if an item is a bundle item
const isBundleItem = function(item) {
	if (!item) return false;
	return getItemType(item) === "bundle";
};

//Checks if an item is a bundle item with a beast-bulk-codes sku instead of a
//beast-yearly sku
const isBundleItemWithCode = function(item) {
	if (!item) return false;
	return (
		isBundleItem(item) &&
		item.options.bundleIDs[0]?.includes(SUBSCRIPTION_CODE_SKU)
	);
};

const isFullSet = function(item) {
	if (!item) return false;
	return _.includes(item.sku, "full-set");
};

const isScienceFullSet = function(item) {
	if (!item) return false;
	return _.includes(item.sku, "full-set-science");
};

const isApparelItem = function(item) {
	if (!item) return false;
	return getItemType(item) === "apparel";
};

const isCodeItem = function(item) {
	if (!item) return false;
	return getItemType(item) === "code";
};

const isGiftItem = function(item) {
	if (!item) return false;
	return getItemType(item) === "gift";
};

const filterSKUsByType = (skus, type) =>
	skus.filter(sku => getTypeFromSKU(sku) === type);

// Gets the right display name for multiple bundles
// can take bundleItem or array of grades
const getMultiBundleName = function(bundleItemOrGrades) {
	let gradeNumbers;
	if (_.isArray(bundleItemOrGrades)) {
		gradeNumbers = bundleItemOrGrades;
	} else {
		gradeNumbers = bundleItemOrGrades.options.bundleIDs.map(g =>
			StoreBundleUtil.getBundleGrade(g)
		);
	}
	const ordered = gradeNumbers.sort();
	let g1, g2, yr;
	let previewStr = "";
	if (!ordered.length) return "";
	// If multiple grades, ex: BA 3-4 + 2 yrs BA Online OR BA 3, 5 + 2 yrs BA Online
	// If single grade, ex: BA 3A-3D + 1 yr BA Online
	if (ordered.length === 1) {
		g1 = ordered[0];
		previewStr += "BA " + g1 + "A-" + g1 + "D";
		yr = "yr";
	} else {
		g1 = _.first(ordered);
		g2 = _.last(ordered);
		if (g2 - g1 === ordered.length - 1) {
			// If continuous grades
			previewStr += "BA " + g1 + "-" + g2;
		} else {
			previewStr += "BA " + ordered.map(g => g).join(", ");
		}
		yr = "yrs";
	}
	previewStr += " + " + ordered.length + " " + yr + " BA Online";
	return previewStr;
};

// Gets the item display name for an upgrade, given all the info in upgradeInfo
// object
const getUpgradeItemName = function(upgradeInfo) {
	const {previousType, newType} = upgradeInfo;
	return `Upgrade ${_.capitalize(previousType)} to ${_.capitalize(newType)}`;
};

//Checks if an item gets a discount when multiple are purchased
const isSubscriptionWithMultiPrice = function(item) {
	const skuData = ShopData[item.sku];
	const isBundle = !!(item.options && item.options.bundleID);
	return (
		skuData &&
		skuData.type === "subscription" &&
		skuData.multiPrice &&
		skuData.multiPrice < skuData.price &&
		!isBundle
	);
};

// Checks if item is a renewable subscription
const isRenewable = function(item) {
	const isBundle = !!(item.options && item.options.bundleID);
	return isSubscriptionItem(item) && !isBundle;
};

//Checks if an item type is shippable
const isItemTypeShippable = function(type) {
	return _.includes(SHIPPABLE_TYPES, type);
};

//Checks if an item is shippable
const isItemShippable = function(item) {
	return isItemTypeShippable(getItemType(item));
};

// Checks if an item is in stock and visible
const isItemEnabled = function(item) {
	if (!item.sku) return false;

	// If the item is really a fee, just check that it is a valid sku in feeData.json.
	if (isFeeItem(item)) {
		return FeeData.hasOwnProperty(item.sku);
	}
	if (ShopBundleData[item.sku]) {
		return ShopBundleData[item.sku].skus.every(
			sku => ShopData[sku].visible && ShopData[sku].inStock
		);
	}
	if (!ShopData.hasOwnProperty(item.sku)) {
		return false;
	}
	if (ShopData[item.sku].visible && ShopData[item.sku].inStock) {
		return true;
	}
	return false;
};

// Try to determine if subscription is yearly or monthly based on cartItem
const getSubscriptionPlanType = function(item) {
	return ShopData[item.sku].planType;
};

// Get item's subscription rate
const getSubscriptionRateID = function(item) {
	if (isSubscriptionItem(item)) {
		if (item.options && item.options.bundleID) {
			if (ShopData[item.sku].subscriptionBundleRateID) {
				return ShopData[item.sku].subscriptionBundleRateID;
			}
		}
		return ShopData[item.sku].subscriptionRateID;
	}
	return 0;
};

//Gets the list of options that an item has
//ie {sku: "beast-3a", quantity: 3} => [] and {sku: "tshirt-grogg", quantity: 1, size: "large", color: "red"} => ["size", "color"]
const getOptions = function(item) {
	return _.keys(item.options);
};

//Gets the list of options that an item has sorted alphabetically
const getSortedOptions = function(item) {
	return getOptions(item).sort();
};

//Checks if an item has any extra options set on it
//Gets the list of options that an item has
//ie {sku: "beast-3a", quantity: 3, options: {}} => false and {sku: "tshirt-grogg", quantity: 1, options: {size: "large", color: "red"}} => true
const hasOptions = function(item) {
	return !_.isEmpty(item.options);
};

// Internal helper to calculateItemPrices. Returns {price, quantity}. Only
// additional option relative to calculateItemPrices:
// - multiSub: If true, apply the multiple subscription discount.
const calculateOneItemPrice = function(item, options = {}) {
	const {
		perItem = false,
		forTax = false,
		skipDiscounts = false,
		multiSub = false,
		forShipping = false,
		recomputePrice = false,
		giftItemCount = 0,
	} = options;
	const {sku, quantity, options: itemOptions = {}} = item;

	// If the cart has full sets, we should pull out the individual books
	// so they are included in the quantity and count
	// towards a discount

	const itemData = getItemDataFromSku(sku);
	let finalQuantity = quantity;
	let basePrice = Big(itemData.price);
	if (!recomputePrice && item.hasOwnProperty("price") && !forShipping) {
		basePrice = Big(item.price);
	}
	if (!recomputePrice && skipDiscounts && itemData && itemData.fullPrice) {
		basePrice = Big(itemData.fullPrice);
	}
	if (
		itemOptions &&
		itemOptions.bundleID &&
		itemData.subscriptionBundlePrice &&
		!forShipping
	) {
		basePrice = Big(itemData.subscriptionBundlePrice);
	}

	let totalPrice = basePrice;
	if (!perItem && !forTax) {
		if (!_.isFinite(quantity)) {
			SharedErrorLog.logError(
				"E_BAD_QUANTITY",
				"quantity of item in store was not a finite number",
				{quantity, item}
			);
		}
		totalPrice = totalPrice.times(quantity);
	}
	if (
		(isBookItem(item) || isCodeItem(item) || isGiftItem(item)) &&
		!skipDiscounts &&
		!forShipping
	) {
		let qtyForDiscount = item.totalQuantity || quantity;
		if (isGiftItem(item)) {
			qtyForDiscount = giftItemCount;
		}
		let basePriceWithDiscounts = basePrice;
		if (qtyForDiscount >= 10) {
			basePriceWithDiscounts = basePrice.times(0.8).round(2, Big.roundDown);
			if (forTax || perItem) {
				totalPrice = basePriceWithDiscounts;
			} else {
				totalPrice = basePriceWithDiscounts.times(quantity);
			}
		} else if (qtyForDiscount >= 5) {
			basePriceWithDiscounts = basePrice.times(0.9).round(2, Big.roundDown);
			if (forTax || perItem) {
				totalPrice = basePriceWithDiscounts;
			} else {
				totalPrice = basePriceWithDiscounts.times(quantity);
			}
		}
		totalPrice = Big(totalPrice.toFixed(2));
	} else if (isSubscriptionWithMultiPrice(item) && !skipDiscounts) {
		const multiPrice = Big(itemData.multiPrice);
		totalPrice = multiSub ? multiPrice : basePrice;
		if (item.quantity > 1) {
			totalPrice = totalPrice.add(multiPrice.mul(item.quantity - 1));
		}
		if (forTax) {
			finalQuantity = 1;
		} else if (perItem) {
			totalPrice = totalPrice.div(item.quantity);
		}
	}

	return {price: totalPrice, quantity: finalQuantity};
};

// Returns the same cartItems array except with a price field on each item.
// In extreme cases, the quantity of each item may also be altered. Options:
// - perItem: If true, give the price for 1 item (quantity may
//   still be used for discounts).
// - forTax: If true, compute prices for tax, very similar to perItem true.
//   This may set item quantities to 1 to deal with weird multiprice cases.
// - skipDiscounts: If true, ignore discounts for buying books in bulk.
// - forShipping: If true, calculate all prices without discounts, including
//   discounts as part of a bundle.

const calculateItemPrices = function(cartItems, options) {
	const optionsWithMulti = _.extend({}, options, {multiSub: true});
	const optionsWithSkipDiscounts = _.extend({}, options, {skipDiscounts: true});
	const seenSubSKUs = {};
	const giftItemCount = cartItems.filter(item => isGiftItem(item)).length;
	// track all books and subscription codes that are part of a bundle
	// Bundled subscriptions with multiprice discounts don't get discounted, so
	// if there are any, preemptively set that sku as seen.
	cartItems.forEach(i => {
		if (i.options && i.options.bundleID && isSubscriptionItem(i)) {
			seenSubSKUs[i.sku] = true;
		}
	});
	return cartItems.map(i => {
		let optionsForThisItem = options;
		// skip the discount for anything in a code bundle, since these already
		// receive the bundle discount (do not apply the 5 or 10 quantity discount
		// for codes/booksets)
		if (i.options?.bundleID?.includes(SUBSCRIPTION_CODE_SKU)) {
			optionsForThisItem = optionsWithSkipDiscounts;
		}
		if (isSubscriptionWithMultiPrice(i)) {
			if (seenSubSKUs[i.sku]) {
				optionsForThisItem = optionsWithMulti;
			} else {
				seenSubSKUs[i.sku] = true;
			}
		} else if (isGiftItem(i)) {
			optionsForThisItem = {...options, giftItemCount: giftItemCount};
		}
		return _.extend({}, i, calculateOneItemPrice(i, optionsForThisItem));
	});
};

const getPrice = (sku, fullPrice) => {
	const priceField = fullPrice ? "price" : "subscriptionBundlePrice";

	return ShopData[sku][priceField];
};
/**
 * Calculate the total bundle price of books and subscription.
 *
 * @param {string} bundleID, as specified in dbShopBundle.json
 * @param {boolean} fullPrice, if true, calculate the full price of the bundle
 * @param {boolean | undefined} isBulkCodeBundle, if true, this is a bulk code bundle. This is mostly called recursively
 *   because the results for a bulk code bundle should be the same as the results for the bundle without the bulk code, BUT
 *   should still acknowledge that it is a bulk *code*.
 * @return {Object} {Big books, Big subscription, Big total}
 */
/**
/**
 * Calculate the total bundle price of books and subscription.
 *
 * @param {string} bundleID, as specified in dbShopBundle.json
 * @return {Object} {Big books, Big subscription, Big total}
 */
const calculateBundlePrice = function(bundleID, fullPrice) {
	// helpers
	const getCurriedPrice = sku => getPrice(sku, fullPrice);
	const getBigSum = arr => arr.reduce((t, p) => t.add(p), new Big(0));

	const hasBulkCode = bundleID.includes(SUBSCRIPTION_CODE_SKU);

	const skus = getSKUsFromBundleID(bundleID);
	const books = getBigSum(filterSKUsByType(skus, "book").map(getCurriedPrice));
	const subscription = getBigSum(
		filterSKUsByType(skus, hasBulkCode ? "code" : "subscription").map(
			getCurriedPrice
		)
	);
	const total = getBigSum(skus.map(getCurriedPrice));
	return {
		books,
		subscription,
		total,
	};
};

//Functions that run on a list of cartItems:

//Gets the total number of items in the cart, with book sets counted as a single item
const getItemCountWithBundlesAndSets = function(cartItems) {
	const updatedItems = StoreBundleUtil.getItemsWithBundles(cartItems);
	const bookItems = getBookItems(updatedItems);
	const nonBookItems = updatedItems.filter(item => !isBookItem(item));
	const groupedBooks = groupBooksIntoSets(bookItems);
	return getItemCount(
		StoreBundleUtil.getItemsWithBundles(groupedBooks.concat(nonBookItems))
	);
};

//Gets the total number of items in the cart (not the number of types of items)
const getItemCountWithBundles = function(cartItems) {
	return getItemCount(StoreBundleUtil.getItemsWithBundles(cartItems));
};

//ie [{sku: "beast-3a", quantity: 3}, {sku: "tshirt-grogg", quantity: 1, size: "large", color: "red"}] => 4
const getItemCount = function(cartItems) {
	return _.sumBy(cartItems, i => i.quantity);
};

//Gets the total number of items in the cart that need to be shipped
//ie [{sku: "beast-3a", quantity: 3}, {sku: "giftcard", quantity: 1}] => 3
const getShippingItemCount = function(cartItems) {
	return getItemCount(cartItems.filter(isItemShippable));
};

// Get all book items in cartItems (may include bundles)
const getBookItems = function(cartItems) {
	return cartItems.filter(item => isBookItem(item));
};

const getScienceItems = function(cartItems) {
	return cartItems.filter(item => isScienceItem(item));
};

// checks for any type of subscription item
// should include bundles and any code that redeems for a subscription
const getAnySubscriptionTypeItems = function(cartItems) {
	return cartItems.filter(item => isAnySubscriptionTypeItem(item));
};

// Get all subscription items in cartItems (beast-monthly, beast-yearly; excludes bundle type)
const getSubscriptionItems = function(cartItems) {
	return cartItems.filter(item => isSubscriptionItem(item));
};

const getItemsWithSKU = function(cartItems, sku) {
	return cartItems.filter(item => item.sku === sku);
};

// Get all bundle items in cartItems (may include bundles)
const getBundleItems = function(cartItems) {
	return cartItems.filter(item => isBundleItem(item));
};

const getApparelItems = function(cartItems) {
	return cartItems.filter(item => isApparelItem(item));
};

const getCodeItems = function(cartItems) {
	return cartItems.filter(item => isCodeItem(item));
};

const getGiftItems = function(cartItems) {
	return cartItems.filter(item => isGiftItem(item));
};

const getSortedBookItems = function(cartItems) {
	return _.sortBy(getBookItems(cartItems), ["sku"]);
};

const getSortedSubscriptionItems = function(cartItems) {
	return _.sortBy(getSubscriptionItems(cartItems), ["sku"]);
};

/** 0 -> A, 1 -> B, ..., 25 -> Z, 26 -> AA, 27 -> AB, ... */
// Used for placeholder names for subscriptions in the cart
// This should not be shown to the user, it's just to have a uninque
// name for the subscription so we can add it to the cart,
// and so that we can distinguish placeholder names from user-entered names
const getSubscriptionPlaceholderName = function(index) {
	let result = "";
	let n = index;
	while (n >= 0) {
		result = String.fromCharCode(65 + (n % 26)) + result;
		n = Math.floor(n / 26) - 1;
	}
	return "Student " + result;
};

const getSortedBundleItems = function(cartItems) {
	return _.sortBy(getBundleItems(cartItems), ["sku"]);
};

const getSortedApparelItems = function(cartItems) {
	return _.sortBy(getApparelItems(cartItems), ["sku"]);
};

const getSortedCodeItems = function(cartItems) {
	return _.sortBy(getCodeItems(cartItems), ["sku"]);
};

const getSortedGiftItems = function(cartItems) {
	return _.sortBy(getGiftItems(cartItems), ["sku"]);
};

const getSortedCartItems = function(cartItems) {
	const items = StoreBundleUtil.getItemsWithBundles(cartItems);
	return _.concat(
		getSortedBookItems(items),
		getSortedSubscriptionItems(items),
		getSortedBundleItems(items),
		getSortedApparelItems(items),
		getSortedCartItems(items)
	);
};

//Checks if a cart has items that must be shipped
const hasShippableItems = function(cartItems) {
	return !!getShippingItemCount(cartItems);
};

// Checks if a cart has any items that are any type of subscription (cartitems are already "bundled")
const hasAnySubscriptionTypeItems = function(cartItems) {
	return !!getAnySubscriptionTypeItems(cartItems).length;
};

//Checks if a cart has subscriptions (cartitems are already "bundled")
const hasSubscriptionItems = function(cartItems) {
	return !!getSubscriptionItems(cartItems).length;
};

//Checks if a cart has any deferred bundle subscription lines
const hasDeferredBundleSubscriptions = function(cartItems) {
	return cartItems.some(isDeferredBundleSubscription);
};

//Checks if a cart has bundles (cartitems are already "bundled")
const hasBundleItems = function(cartItems) {
	return !!getBundleItems(cartItems).length;
};

//Checks if a cart has books (cartitems are already "bundled")
const hasBookItems = function(cartItems) {
	return !!getBookItems(cartItems).length;
};

//Checks if a cart has books from certain grade
const hasBookItemsInBALevel = function(cartItems, baLevel) {
	const patt = new RegExp("^beast-" + baLevel);
	return _.some(getBookItems(cartItems), item => patt.test(item.sku));
};

//Checks if a cart has science book items
const hasScienceItems = function(cartItems) {
	return !!getScienceItems(cartItems).length;
};

//Checks if a cart has apparel
const hasApparelItems = function(cartItems) {
	return !!getApparelItems(cartItems).length;
};

//Checks if a cart has bulk codes
const hasCodeItems = function(cartItems) {
	return !!getCodeItems(cartItems).length;
};

//Checks if a cart has ONLY bulk codes
const hasOnlyCodeItems = function(cartItems) {
	return getCodeItems(cartItems).length === cartItems.length;
};

const hasOnlySubscriptionCodeItems = function(cartItems) {
	return (
		getGiftItems(cartItems).length + getCodeItems(cartItems).length ===
		cartItems.length
	);
};

//Checks if a cart has gift codes
const hasGiftItems = function(cartItems) {
	return !!getGiftItems(cartItems).length;
};

//Checks if cart items include monthly subscription
const hasMonthlySubscriptions = function(cartItems) {
	return !!_.find(cartItems, i => {
		return isSubscriptionItem(i) && getSubscriptionPlanType(i) === "monthly";
	});
};

const hasYearlySubscriptions = function(cartItems) {
	return !!_.find(cartItems, i => {
		return isSubscriptionItem(i) && getSubscriptionPlanType(i) === "yearly";
	});
};

const hasBAOSupportedBook = cartItems => {
	return (
		_.filter(cartItems, item => /beast-[1-5][a-d]/.test(item.sku)).length > 0
	);
};

const hasItemWithSKU = function(cartItems, sku) {
	return _.some(cartItems, item => item.sku === sku);
};

const getBookSKUsForBundleID = bundleID => {
	const skus = getSKUsFromBundleID(bundleID);
	return filterSKUsByType(skus, "book");
};

const getBundleSubitems = function(bundleItem) {
	const hasBulkCode = isBundleItemWithCode(bundleItem);
	const bundleIDs = bundleItem.options.bundleIDs;
	const subitems = {
		subscription:
			"BA Online " +
			bundleIDs.length +
			"-year subscription" +
			(hasBulkCode ? " code" : ""),
		books: bundleItem.options.bundleIDs.map(
			bundleID =>
				`Full BA ${StoreBundleUtil.getBundleGrade(bundleID)} book set (${
					getBookSKUsForBundleID(bundleID).length
				} books)`
		),
	};
	return subitems;
};

const getSubscriptionNames = function(cartItems) {
	const names = cartItems.map(item => _.toLower(item.options.displayName));
	return _.uniq(names.filter(Boolean));
};

// Returns whether cart can add more subscriptions. Returning false means the
// limit was hit.
const canAddMoreSubscriptions = function(cartItems) {
	return getSubscriptionNames(cartItems).length < MAX_SUBSCRIPTIONS_FOR_PARENT;
};

// Returns whether cart has gone over limit for subscriptions. Intended as a
// check after an items update.
const hasTooManySubscriptions = function(cartItems) {
	return getSubscriptionNames(cartItems).length > MAX_SUBSCRIPTIONS_FOR_PARENT;
};

const allowedSubNameChars =
	"abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ -";
const allowedNamePattern = new RegExp(`^[${allowedSubNameChars}]*$`);
// Returns a flat string that can be used to filter characters in a text input
// for subscription display name.
const getAllowedSubscriptionNameCharacters = function() {
	return allowedSubNameChars;
};

// Internal helper for getBadNameInSubscriptions. Returns null if the name has
// invalid characters.
const cleanSubscriptionName = function(name) {
	if (!allowedNamePattern.test(name)) return null;
	return name.replace(/[^a-zA-Z]/g, "").toLowerCase();
};

/**
 * Determines if name is a bad subscription name among an array of subscription names
 * @param {Array} names array of cleaned names or objects with a name or cleanedName attribute
 * @param {String} name
 * @returns {String|Null} error type, or null if no error
 */
const isBadNameInSubscriptions = function(
	names,
	name,
	isCleaned = false,
	preAddToCart = false
) {
	if (names.length === 0) return null;
	if (!isCleaned && !name) return "E_MISSING";

	const cleanedName = isCleaned ? name : cleanSubscriptionName(name);
	if (!cleanedName) return "E_BAD_CHAR";
	if (name.length > MAX_DISPLAY_NAME_LENGTH) return "E_LENGTH";

	// convert to an array of cleaned names, if it isn't already
	let cleanedNames;
	if (_.isObject(names[0])) {
		cleanedNames = names.map(obj => (isCleaned ? obj.cleanedName : obj.name));
	} else {
		cleanedNames = isCleaned ? names : names.map(n => cleanSubscriptionName(n));
	}

	if (preAddToCart) {
		if (cleanedNames.indexOf(cleanedName) !== -1) return "E_DUPLICATE_NAME";
	} else if (
		cleanedNames.indexOf(cleanedName) !== cleanedNames.lastIndexOf(cleanedName)
	) {
		return "E_DUPLICATE_NAME";
	}

	return null;
};

// Pass an array of subscription names. Returns {name, error} if there is a
// bad name, else null.
const getBadNameInSubscriptions = function(names) {
	const cleanedNameObjs = names.map(name => {
		return {
			name,
			cleanedName: cleanSubscriptionName(name),
		};
	});

	for (const nameObj of cleanedNameObjs) {
		if (!nameObj.name) return {name: nameObj.name, error: "E_MISSING"};
		const error = isBadNameInSubscriptions(
			cleanedNameObjs,
			nameObj.cleanedName,
			true
		);
		if (error) return {name: nameObj.name, error};
	}
	return null;
};

// getBadNameInSubscriptions when given an array of cart items (not necessarily
// all subscriptions) instead of an array of names. Returns null or {item,
// error}.
const getBadNameInSubscriptionItems = function(cartItems) {
	// Don't flag a name as a duplicate if it has multiple bundles stacked.
	cartItems = StoreBundleUtil.getItemsCollapsedBundles(cartItems);
	const subItems = getSubscriptionItems(cartItems);
	const names = subItems.map(sub => sub.options.displayName);

	const duplicateItems = subItems.filter(sub => sub.quantity > 1);
	if (duplicateItems.length > 0) {
		// just error for the first item
		return {item: duplicateItems[0], error: "E_DUPLICATE_NAME"};
	}

	const error = getBadNameInSubscriptions(names);
	if (error) {
		const item = subItems.find(
			subItem => subItem.options.displayName === error.name
		);
		return {item, error: error.error};
	}

	return null;
};

//Calculates the subtotal for the cart (the sum of the costs of all items, but not taxes and shipping)
const calculateSubtotal = function(cartItems) {
	const withPrices = calculateItemPrices(cartItems, false);
	return cartItems.reduce(
		(total, item, i) => total.add(withPrices[i].price),
		Big(0)
	);
};

//Calculates the subtotal for items of a certain type in the cart
const calculateSubtotalForType = function(cartItems, type) {
	return calculateSubtotal(cartItems.filter(ci => getItemType(ci) === type));
};

//Calculates subtotal for all shippable items
const calculateSubtotalForShipping = function(cartItems) {
	const shippableItems = cartItems.filter(isItemShippable);
	const withPrices = calculateItemPrices(shippableItems, {forShipping: true});
	return shippableItems.reduce(
		(total, item, i) => total.add(withPrices[i].price),
		Big(0)
	);
};

//Taken from aops3 m/store/includes/class.Cart.php
const calculateShipping = function(cartItems, method, options = {}) {
	// If subtotal is passed in through options, it must be a subtotal for
	// shippable items only.
	const subtotal = options.subtotal || calculateSubtotalForShipping(cartItems);
	const count = options.count || getShippingItemCount(cartItems);
	if (subtotal.lte(0) || count === 0) {
		return Big(0);
	} else {
		switch (method) {
			case "us-economy":
				const overFreeShippingThreshold = subtotal.gte(100);
				return Big(overFreeShippingThreshold ? 0 : 5.99);
			case "us-priority":
				return Big(7.99).plus(count);
			case "canada":
				return Big(subtotal)
					.times(0.15)
					.plus(25);
			case "international":
				return Big(subtotal)
					.times(0.3)
					.plus(29);
			default:
				throw new Error("E_UNKNOWN_SHIPPING_METHOD");
		}
	}
};

const calculateTotal = function(prices) {
	let total = Big(prices.subtotal || 0).minus(prices.couponApplied || 0);
	if (total.lt(0)) {
		total = Big(0);
	}
	total = total.plus(prices.shipping || 0).plus(prices.tax || 0);
	return total.toFixed(2);
};

const getItemPriceBig = function(cartItems, item, options) {
	if (item.price) {
		return Big(item.price);
	} else if (isBundleItem(item)) {
		const bundlePrices = item.options.bundleIDs.map(
			id => calculateBundlePrice(id).total
		);
		const price = bundlePrices.reduce((t, i) => t.plus(i), Big(0));
		// subscription code bundles can have quantity > 1,
		// but regular subscription bundles cannot
		return price.times(item.quantity);
	} else if (isFullSet(item)) {
		const bookItems = _.cloneDeep(item.subitems);
		// If we also have individual books for the books that are in this set
		// we should have the totalQuantity and use that
		// For each of the subitems, go through the cart items and see if there
		// are others, and add that quantity to these book items
		const bookPrices = calculateItemPrices(bookItems, options);
		const price = bookPrices.reduce((t, i) => t.plus(i.price), Big(0));
		return price;
	} else {
		// If we have full sets in the cart, we should reduce the quantity here
		// that we are using to compute the prices with
		const withPrices = calculateItemPrices(cartItems, options);
		const itemPrice = _.find(
			withPrices,
			p => item.sku === p.sku && _.isEqual(item.options, p.options)
		);
		// When we have extra copies of a book from a full set,
		// the quantity found in the whole cart may be larger than
		// the quantity that we're trying to compute the price for.
		// item.quantity is the quantity of books in the current
		// grouping, and itemPrice.quantity is the total quantity
		// of this book in the entire cart (not including books
		// associated with a bundle)
		// For example, if we have 3 full BA1 sets and 2 more
		// separate 1A books, itemPrice is computed with all 5 of
		// the books, so itemPrice.quantity is 5
		// item.price is computed with the items in the current grouping
		// and this will be 2
		// So we divide the TOTAL PRICE of all the books by the total
		// quantity so that we get the PRICE PER BOOK
		// then multiply by the number of books in THIS grouping
		// to get the price that is contributed by this grouping
		// of books
		let price = itemPrice.price;
		if (item.quantity && item.quantity !== itemPrice.quantity) {
			price = price.div(itemPrice.quantity);
			price = price.times(item.quantity);
		}
		return price;
	}
};

const getItemPriceString = function(cartItems, item, options) {
	const price = getItemPriceBig(cartItems, item, options);
	return toPrice(price);
};

/**
 * Retrieve total item prices for use in calculating coupon discounts and taxes
 *
 * @param {Array} cartItems
 * @param {Object} coupon
 * @return {Object}
 *    preCouponPrices:Big[] - Total item prices without coupons. For instance,
 *      if 3 copies of beast-3a, then 45
 *    prices:Big[] - Total item prices after coupons were applied.
 *    remaining:Big - Amount remaining on coupon
 *    applied:Big - Amount applied to order
 */
const getTotalItemPrices = function(cartItems, coupon) {
	const withPrices = calculateItemPrices(cartItems, {forTax: true});
	const prices = withPrices.map(item =>
		new Big(item.price).times(item.quantity)
	);
	if (coupon) {
		const withCoupon = CouponUtil.getAppliedPrices(coupon, cartItems, prices);
		return _.extend({}, withCoupon, {preCouponPrices: prices});
	}

	return {
		preCouponPrices: prices,
		prices,
		remaining: new Big(0),
		applied: new Big(0),
	};
};

/**
 * If the item has a fullPrice in the db (which means its current price is
 * already a discounted price)
 */
const itemHasFullPrice = function(item) {
	return (
		item && item.sku && ShopData[item.sku] && !!ShopData[item.sku].fullPrice
	);
};

/**
 * Retreive list of TICs (Tax information codes) required for order
 *
 * @param {Object} cartItems
 * @return {Array} List of TICs, such as ["00000", "11010", "30070"]
 */
const getTICs = function(cartItems) {
	const TICs = [];
	if (hasBookItems(cartItems)) TICs.push(getTaxCodes().book);
	if (hasSubscriptionItems(cartItems)) TICs.push(getTaxCodes().subscription);
	if (hasShippableItems(cartItems)) TICs.push(getTaxCodes().shipping);
	if (hasApparelItems(cartItems)) TICs.push(getTaxCodes().apparel);
	if (hasCodeItems(cartItems)) TICs.push(getTaxCodes().code);
	if (hasGiftItems(cartItems)) TICs.push(getTaxCodes().gift);

	if (_.some(cartItems, i => isPuzzleItem(i))) {
		TICs.push(getTaxCodes().puzzlebook);
	}
	if (_.some(cartItems, i => isFeeItem(i))) {
		TICs.push(getTaxCodes().shippingfee);
	}
	return TICs;
};

const getTIC = function(item) {
	let type = getTypeFromSKU(item.sku);
	if (isPuzzleItem(item)) {
		type = "puzzlebook";
	}
	if (type && _.has(getTaxCodes(), type)) {
		return getTaxCodes()[type];
	}
	return getTaxCodes().book;
};

// nexus: shipping or billing address
const getCustomerID = function(nexus) {
	const namePieces = nexus.name.replace(/\s+/g, " ").split(" ");
	let lastName = namePieces.pop();
	let firstName = namePieces.join(" ");
	const country = nexus.country;
	let zip = nexus.zipcode || "";

	if (!country || country === "US") {
		zip = nexus.zipcode.substr(0, 5);
	} else if (country === "CA") {
		zip = nexus.zipcode.substr(0, 7);
	}
	// Avalara requires a customer code that is max 50 characters
	// need 3 spots for the comma and spaces in the final customerID
	const maxLength = 47;
	// need 1 more space to keep first name with minimum length of 1, so subtract
	// it from the last name length
	const maxLastLength = maxLength - zip.length - 1;
	// first name should always be at least 1 character
	const maxFirstLength = Math.max(0, maxLastLength - lastName.length) + 1;
	lastName = lastName.substr(0, maxLastLength);
	firstName = firstName.substr(0, maxFirstLength);
	return `${lastName}, ${firstName} ${zip}`;
};

/*
Example of items storage:

Frontend items array version, always called cartItems:
[
  {sku: "beast-3a", quantity: 3, options: {}},
  {sku: "beast-3a-practice", quantity: 3, options: {}},
  {sku: "beast-4a", quantity: 1, options: {bundleID: "bundle-grade-4"}},
  {sku: "beast-4b", quantity: 1, options: {bundleID: "bundle-grade-4"}},
  {sku: "beast-4c", quantity: 1, options: {bundleID: "bundle-grade-4"}},
  {sku: "beast-4d", quantity: 1, options: {bundleID: "bundle-grade-4"}},
  {sku: "beast-4a-practice", quantity: 1, options: {bundleID: "bundle-grade-4"}},
  {sku: "beast-4b-practice", quantity: 1, options: {bundleID: "bundle-grade-4"}},
  {sku: "beast-4c-practice", quantity: 1, options: {bundleID: "bundle-grade-4"}},
  {sku: "beast-4d-practice", quantity: 1, options: {bundleID: "bundle-grade-4"}},
  {sku: "beast-yearly", quantity: 1, options: {bundleID: "bundle-grade-4"}},
  {sku: "beast-5b", quantity: 8, options: {}},
  {sku: "tshirt-grogg", quantity: 1, options: {size: "small", color: "orange"}},
  {sku: "tshirt-grogg", quantity: 1, options: {size: "large", color: "blue"}},
  {sku: "tshirt-grogg", quantity: 1, options: {size: "large", color: "orange"}}
]

Serialized items object version, always called serializedCartItems:
{
  total: 17,
  beast-3a: 3,
  beast-3a-practice: 3,
  beast-5b: 8,
  tshirt-grogg: 3,
  tshirt-grogg,orange,small: 1,
  tshirt-grogg,blue,large: 1,
  tshirt-grogg,orange,large: 1
}
*/

// Used to convert the frontend storage of items (an array with keys sku, quantity, and any options on the items)
// to the database storage of items (an object with total, skus of all items mapped to quantity, and sku with options mapped to quantity)
const serializeCartItems = function(cartItems) {
	const iObj = {total: 0};
	_.forEach(cartItems, i => {
		//Add the quantity of that item to the total number of items
		iObj.total += i.quantity;
		//Then to the tally of items with that sku
		if (iObj[i.sku]) {
			iObj[i.sku] += i.quantity;
		} else {
			iObj[i.sku] = i.quantity;
		}
		//Then, if that item has any options, add the quantity for that specific item with its options
		if (hasOptions(i)) {
			const options = getSortedOptions(i).map(opt => i.options[opt]);
			const iKey = _.concat(i.sku, options).join(",");
			iObj[iKey] = i.quantity;
		}
	});
	_.forEach(StoreBundleUtil.getBundleIDs(cartItems), id => {
		iObj[id] = (iObj[id] || 0) + 1;
	});
	return iObj;
};

const requiresPriorityShipping = function(state) {
	return state && _.includes(PRIORITY_SHIPPING_STATES, state);
};

const getDefaultShippingType = function(country) {
	if (country === USA) {
		return ""; //No default for US, user must pick
	} else if (country === CANADA) {
		return "canada";
	} else {
		return "international";
	}
};

// getInputError helper
const checkLength = function(value, minLength, maxLength) {
	const error = {type: "E_LENGTH"};
	if (minLength != null) error.min = minLength;
	if (maxLength != null) error.max = maxLength;
	if (minLength != null && value.length < minLength) return error;
	if (maxLength != null && value.length > maxLength) return error;
	return null;
};

// Adapted from aops3 m/store/shipping-information.php
// Returns null if okay, an object {type, ...details} otherwise.
const getInputError = function(type, value) {
	if (!value && !_.isNumber(value)) {
		return null;
	} else if (type !== "comments" && !isValidCheckoutString(value)) {
		return {type: "E_BAD_CHAR"};
	}
	if (
		["us-zipcode", "canada-zipcode", "zipcode"].includes(type) &&
		!isZipcodeCharacters(value)
	) {
		return {type: "E_BAD_CHAR"};
	}
	if (type === "us-zipcode") {
		const ok = /^\d{5}(?:[-\s]\d{4,6})?$/.test(value);
		return ok ? null : {type: "E_INVALID_VALUE"};
	} else if (type === "canada-zipcode") {
		const ok = /^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$/i.test(
			value
		);
		return ok ? null : {type: "E_INVALID_VALUE"};
	} else if (type === "zipcode") {
		return checkLength(value, null, 20);
	} else if (type === "phone") {
		if (!/^[0-9 \+\-()\.]+$/.test(value)) return {type: "E_INVALID_VALUE"};
		const phone = value.replace(/[^0-9]+/g, "");
		return checkLength(phone, 4, 20);
	} else if (type === "name") {
		return checkLength(value, 4, 64);
	} else if (type === "comments") {
		return checkLength(value, null, 1000);
	} else if (type === "address1") {
		return checkLength(value, 4, 100);
	} else if (type === "address2") {
		return checkLength(value, 2, 100);
	} else if (type === "city") {
		return checkLength(value, 2, 40);
	} else if (type === "state") {
		return checkLength(value, null, 30);
	} else if (type === "country") {
		return checkLength(value, 2, 2);
	} else if (type === "email") {
		const ok = isValidEmail(value);
		if (!ok) return {type: "E_INVALID_VALUE"};
		return checkLength(value, null, 100);
	} else if (type === "number") {
		const ok = isValidCardNumber(value);
		return ok ? null : {type: "E_INVALID_VALUE"};
	} else if (type === "expMonth") {
		const ok = value >= moment().month();
		return ok ? null : {type: "E_INVALID_VALUE"};
	} else if (type === "cvv") {
		return checkLength(value, 3, 4);
	} else {
		return null;
	}
};

// Returns an object with missing, invalid, errorTypes, isComplete.
// - missing: Array of all fields that are missing.
// - invalid: Array of all
const checkSection = function(sectionData, options = {}) {
	const {
		addMissing = false,
		origErrorTypes = {},
		skipFields = {},
		additionalRequiredFields = {},
	} = options;
	const missing = getMissingFields(
		sectionData,
		skipFields,
		additionalRequiredFields
	);
	const errorTypes = getInvalidFields(sectionData, skipFields);
	const invalid = _.keys(errorTypes);
	// Only add missing fields if that option is set or it was marked missing.
	missing.forEach(n => {
		if (addMissing || _.get(origErrorTypes, [n, "type"]) === "E_MISSING") {
			errorTypes[n] = {type: "E_MISSING"};
		}
	});
	return {
		missing,
		invalid,
		isComplete: !missing.length && !invalid.length,
		errorTypes,
	};
};

// Returns array of field names that are missing.
const getMissingFields = function(
	fields,
	skipFields = {},
	additionalRequiredFields = {}
) {
	// Note email appears in both account and contact forms; fortunately same
	// checks needed in both cases.
	let requiredFields = [
		"name",
		"address1",
		"city",
		"country",
		"zipcode",
		"phone",
		"email",
		"number",
		"cvv",
		"expMonth",
		"expYear",
		"method",
		"password",
		"passwordRepeat",
	];
	requiredFields = requiredFields.concat(_.keys(additionalRequiredFields));
	if (
		fields.country &&
		(isCountryUS(fields.country) || isCountryCanada(fields.country))
	) {
		requiredFields.push("state");
	}
	if (fields.country && isCountryBrazil(fields.country)) {
		requiredFields.push("taxpayerID");
	}
	const missingFields = [];
	_.each(requiredFields, f => {
		if (
			fields.hasOwnProperty(f) &&
			!fields[f] &&
			fields[f] !== 0 &&
			!skipFields[f]
		) {
			missingFields.push(f);
		}
	});
	return missingFields;
};

// Returns object mapping bad fields to errors in form {type, ...details}.
const getInvalidFields = function(fields, skipFields = {}) {
	// Note email appears in both account and contact forms; fortunately same
	// checks needed in both cases.
	const invalidFields = {};
	const checkedFields = [
		"name",
		"address1",
		"address2",
		"city",
		"state",
		"country",
		"zipcode",
		"phone",
		"email",
		"comments",
		"number",
		"cvv",
		"expMonth",
		"password",
		"passwordRepeat",
		"taxpayerID",
	];
	checkedFields.forEach(f => {
		const noValue = !fields[f] && fields[f] !== 0;
		if (noValue || skipFields[f]) {
			return;
		} else if (f === "passwordRepeat") {
			const check = checkEmailPassword(fields.password, fields.passwordRepeat);
			if (!check.valid && check.error === "E_PASSWORD_REPEAT_MISMATCH") {
				invalidFields[f] = {type: check.error};
			}
		} else if (f === "password") {
			const check = checkEmailPassword(fields.password, fields.password);
			if (!check.valid) {
				invalidFields[f] = {type: check.error};
			}
		} else if (f === "taxpayerID") {
			const check = checkTaxpayerID(fields.country, fields.taxpayerID);
			if (!check.valid) invalidFields[f] = {type: check.error};
		} else {
			let checkType = f;
			// The next if check used to also look for f === "phone". But country and
			// state are not in the contact form where phone is, so we never got
			// values of us-phone or canada-phone. On noticing this bug, PM and CM
			// decided international phone + US address was a reasonable possibility,
			// so the us-phone and canada-phone validation is now gone.
			if (f === "zipcode") {
				if (isCountryUS(fields.country) && !isStateTerritory(fields.state)) {
					checkType = "us-" + f;
				} else if (isCountryCanada(fields.country)) {
					checkType = "canada-" + f;
				}
			} else if (f === "expMonth") {
				checkType = fields.expYear === moment().year() ? f : null;
			}

			const error = checkType && getInputError(checkType, fields[f]);
			if (error) invalidFields[f] = error;
		}
	});
	return invalidFields;
};

const getCardType = function(ccNumber) {
	ccNumber = ccNumber.replace(/\D/g, "");
	if (ccNumber.length === 15 && ccNumber.substr(0, 1) === "3") {
		return "AMEX";
	}

	if (["60", "62", "64", "65"].includes(ccNumber.substr(0, 2))) {
		return "Discover";
	}

	if (ccNumber.substr(0, 1) === "5") {
		return "MasterCard";
	}

	if (ccNumber.substr(0, 1) === "4") {
		return "Visa";
	}

	return "???";
};

const checkTaxpayerID = function(country, taxpayerID) {
	if (country === BRAZIL) {
		const cpf = /\d{3}\.\d{3}\.\d{3}-\d{2}/.test(taxpayerID);
		const cnpj = /\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/.test(taxpayerID);
		return cpf || cnpj
			? {valid: true}
			: {valid: false, error: "E_TAXPAYER_ID_INVALID"};
	}
	return {valid: true};
};

/**
 * Makes sure the card number satisfies:
 * - Relatively legible (only digits, dashes and space characters and no more than 1 non-digit in a row)
 * - First digit is nonzero
 * - The number is between 8-19 digits
 * - The number is valid according to the Luhn algorithm
 *
 * See https://en.wikipedia.org/wiki/Payment_card_number for more info on card number standards
 *
 * @param {string} ccNumber the credit card number to be checked
 * @returns true if the card should be considered syntactically valid, and false otherwise
 */
const isValidCardNumber = function(ccNumber) {
	if (!/^[ -]?(?:\d[ -]?)+$/.test(ccNumber)) {
		// Allow only digits, spaces and dashes, and never more than one non-digit in a row
		return false;
	}

	// Remove all non-digits
	ccNumber = ccNumber.replace(/\D/g, "");
	const ccDigits = ccNumber.split("").map(d => parseInt(d));

	if (ccDigits.length < 8 || ccDigits.length > 19) {
		// Make sure the length is not too short or too long
		return false;
	}

	if (ccDigits[0] === 0) {
		// Make sure first digit is nonzero
		return false;
	}

	const luhn = digits => {
		let sum = 0;

		[...digits].reverse().forEach((digit, i) => {
			if (i % 2) {
				digit *= 2;
			}
			if (digit > 9) {
				const first = digit % 10;
				const second = Math.floor((digit - first) / 10);
				digit = first + second;
			}
			sum += digit;
		});
		return sum % 10 === 0;
	};

	if (!luhn(ccDigits)) {
		// Make sure it passes the checksum
		return false;
	}

	return true;
};

const isCountryUS = function(country) {
	return country === USA;
};

const isCountryCanada = function(country) {
	return country === CANADA;
};

const isCountryBrazil = function(country) {
	return country === BRAZIL;
};

const isStateTerritory = function(state) {
	return !!states.USTerritories[state];
};

const getShippingMethodInfo = function(method) {
	const table = {
		"us-economy": {type: "US Economy", time: "2-3 weeks"},
		"us-priority": {type: "US Priority", time: "3-7 business days"},
		international: {type: "International", time: "1-3 weeks"},
		canada: {type: "Canada", time: "1-2 weeks"},
	};
	return table[method];
};

const validateAddress = function(address) {
	const state = address.state;
	const country = address.country;
	if (isCountryUS(country)) {
		return states.US.hasOwnProperty(state);
	} else if (isCountryCanada(country)) {
		return states.CA.hasOwnProperty(state);
	}
	return true;
};

const canVerifyAddress = function(shipping) {
	return (
		isCountryUS(shipping.country) &&
		!!shipping.state &&
		!isStateTerritory(shipping.state)
	);
};

const matchesCardingPattern = function(shipping) {
	return !isCountryUS(shipping.country) && states.US[shipping.state];
};

const cleanAddress = function(addressToClean) {
	const address = _.clone(addressToClean);
	if (!address.country || !countries[address.country]) {
		address.country = USA;
	}
	if (isCountryUS(address.country) && !states.US[address.state]) {
		//Clear the state if it doesn't match any of the list of US states & territories
		address.state = "";
	} else if (isCountryCanada(address.country) && !states.CA[address.state]) {
		//Clear the state if it doesn't match any of the list of Canadian states & territories
		address.state = "";
	}
	//Shorten all fields to match our cutoffs. Country is skipped as we check above that
	//it is in the json, and if not make it "US". All countries in the json are only 2
	//characters, so it must already follow the length constraint.
	const lengthCutoffs = {
		name: 64,
		address1: 100,
		address2: 100,
		city: 40,
		state: 40,
		zipcode: 20,
	};
	_.forEach(lengthCutoffs, (cutoff, field) => {
		if (address[field] && address[field].length > cutoff) {
			address[field] = address[field].substring(0, cutoff);
		}
	});
	return address;
};

const isAddressShippable = function(address) {
	const country = address.country;
	if (COUNTRIES_WITH_BLOCKED_SHIPPING.indexOf(country) > -1) {
		return false;
	}
	return true;
};

const expYears = function() {
	const years = _.range(moment().year(), moment().year() + 12);
	return _.zipObject(
		years,
		years.map(y => y.toString())
	);
};

// Returns array of cart items with information for displaying (in emails)
const cartItemsForDisplay = function(cartItems, opts = {}) {
	const {skipValidation = false} = opts;
	const ci =
		opts.prebundledItems ||
		StoreBundleUtil.getItemsWithBundles(cartItems, {skipValidation});
	const bookCartItems = opts.pregroupedItems || ci;
	const getItemDetails = items =>
		items.map(item => {
			const rawStudentName = item.options.displayName;
			const studentName =
				rawStudentName && /^Student [A-Z]+$/i.test(rawStudentName)
					? null
					: rawStudentName;
			let itemName;
			if (opts.upgradeInfo) {
				itemName = getUpgradeItemName(opts.upgradeInfo);
			} else if (isBundleItem(item)) {
				itemName = getMultiBundleName(item);
			} else {
				itemName = getItemName(item);
			}
			let subitems = false;
			if (opts.subitems) {
				subitems = opts.subitems;
			} else if (isFullSet(item) || isScienceFullSet(item)) {
				const subitemsPrices = calculateItemPrices(item.subitems, {
					recomputePrice: true,
				});
				const fullSetPriceTotal = subitemsPrices.reduce(
					(t, i) => t.plus(i.price),
					Big(0)
				);
				item.price = fullSetPriceTotal;
				item.subtotal = fullSetPriceTotal;
			} else if (isBundleItem(item)) {
				subitems = getBundleSubitems(item);
			} else if (isGiftItem(item)) {
				subitems = item.options;
			}

			// IF we have a different quantity here than the total quantity, adjust displayed prices to account
			if (item.totalQuantity && item.totalQuantity !== item.quantity) {
				const subtotalBig = Big(item.subtotal);
				const subtotalPer = subtotalBig.div(item.totalQuantity);
				const newSubtotal = subtotalPer.times(item.quantity);
				item.subtotal = newSubtotal;
			}

			return _.extend({}, item, {
				displayName: itemName + (studentName ? " (" + studentName + ")" : ""),
				subitems,
				price: item.price ? toPrice(item.price) : getItemPriceString(ci, item),
				subtotal: Big(item.subtotal || 0).toFixed(2),
			});
		});

	return {
		books: getItemDetails(getSortedBookItems(bookCartItems)),
		subscriptions: getItemDetails(getSortedSubscriptionItems(ci)),
		bundles: getItemDetails(getSortedBundleItems(ci)),
		apparel: getItemDetails(getSortedApparelItems(ci)),
		codes: getItemDetails(getSortedCodeItems(ci)),
		gifts: getItemDetails(getSortedGiftItems(ci)),
	};
};

module.exports = {
	getNameFromSKU,
	getTypeFromSKU,
	getSKUFromSubscription,
	getSKUsFromBundleID,
	getItemType,
	getItemName,
	getSubscriptionItems,
	getItemsWithSKU,
	getBundleItems,
	getApparelItems,
	getCodeItems,
	getGiftItems,
	getSubscriptionRateID,
	getSubscriptionPlanType,
	isBookItem,
	isPuzzleItem,
	isScienceItem,
	isSubscriptionItem,
	isDeferredBundleSubscription,
	hasDeferredBundleSubscriptions,
	isBundleItem,
	isBundleItemWithCode,
	isFullSet,
	isScienceFullSet,
	isApparelItem,
	isCodeItem,
	isGiftItem,
	getBookItems,
	getScienceItems,
	getSortedBookItems,
	getSortedSubscriptionItems,
	getSubscriptionPlaceholderName,
	getSortedBundleItems,
	getSortedApparelItems,
	getSortedCodeItems,
	getSortedGiftItems,
	getSortedCartItems,
	getMultiBundleName,
	getUpgradeItemName,
	isItemEnabled,
	isSubscriptionWithMultiPrice,
	isRenewable,
	calculateOneItemPrice,
	calculateItemPrices,
	calculateBundlePrice,
	calculateSubtotal,
	calculateSubtotalForType,
	calculateShipping,
	calculateTotal,
	requiresPriorityShipping,
	getDefaultShippingType,
	hasShippableItems,
	hasAnySubscriptionTypeItems,
	hasSubscriptionItems,
	hasBundleItems,
	hasBookItems,
	hasBookItemsInBALevel,
	hasScienceItems,
	hasApparelItems,
	hasCodeItems,
	hasGiftItems,
	hasOnlyCodeItems,
	hasOnlySubscriptionCodeItems,
	hasMonthlySubscriptions,
	hasYearlySubscriptions,
	hasBAOSupportedBook,
	hasItemWithSKU,
	getBookSKUsForBundleID,
	getBundleSubitems,
	getSubscriptionNames,
	canAddMoreSubscriptions,
	hasTooManySubscriptions,
	getAllowedSubscriptionNameCharacters,
	cleanSubscriptionName,
	isBadNameInSubscriptions,
	getBadNameInSubscriptions,
	getBadNameInSubscriptionItems,
	getItemCount,
	getItemCountWithBundles,
	getItemCountWithBundlesAndSets,
	getItemPriceBig,
	getItemPriceString,
	itemHasFullPrice,
	getInputError,
	serializeCartItems,
	checkSection,
	getCardType,
	isValidCardNumber,
	getShippingItemCount,
	isCountryUS,
	isCountryCanada,
	isCountryBrazil,
	isStateTerritory,
	getShippingMethodInfo,
	validateAddress,
	canVerifyAddress,
	matchesCardingPattern,
	cleanAddress,
	isAddressShippable,
	cartItemsForDisplay,
	getTotalItemPrices,
	getCustomerID,
	getTICs,
	getTIC,
	getShippingFeeItemData,
	getShippingFeeItems,
	USA,
	CANADA,
	BRAZIL,
	MONTHS: _.zipObject(_.range(1, 13), moment.months()),
	EXP_YEARS: expYears(),
	STATE_SHIPPING_FEES,
	getTaxCodes,
	COUNTRIES_WITH_BLOCKED_SHIPPING,
};
